import { LitElement, html } from 'lit';

import { api } from '../api/index.js';
import { enrollmentTokenCard } from '../components/enrollment-token.js';
import { confirmAction } from '../components/plane-confirm.js';
import { connectionBadge, serverStatusBadge, stateBadge } from '../components/plane-badge.js';
import { loadState, staleWarning } from '../components/load-state.js';
import { when } from '../components/time.js';
import { revisionLabel } from '../format.js';
import { poll } from '../poll.js';
import { formStyles } from '../../styles/shared/forms.js';
import { pageStyles } from '../../styles/shared/page.js';
import { TableLabels } from '../table-labels.js';
import { tableStyles } from '../../styles/shared/tables.js';
import { enrollmentTokenStyles } from '../../styles/components/enrollment-token.js';
import { generation } from './servers-page.js';
import { serverPageStyles } from '../../styles/pages/server-page.js';

/**
 * One server: what is looked at most first (its connection, the instances assigned to it), then its
 * identity (certificate, enrollment, expected networks), and last what cannot be undone.
 */
/** Why a server is held, by the hold reason of the plane. */
const HOLD_REASON = {
  ahead: (server) =>
    html`It has generation ${server.aheadGeneration}, which this plane never sent: the plane started again with an empty database, or one restored from an older backup.`,
  generation_regression: (server) =>
    html`It refused a desired state as older than its own (generation ${server.aheadGeneration}): the plane went back in time, a database restored from an older backup.`,
  generation_conflict: (server) =>
    html`It refused a desired state with its own generation (${server.aheadGeneration}) and another content: the plane and the clawlet disagree on what that generation is.`,
};

class ServerPage extends LitElement {
  static properties = {
    serverId: { type: String },
    busy: { state: true },
    actionError: { state: true },
    issued: { state: true },
  };

  static styles = [
    pageStyles,
    tableStyles,
    formStyles,
    enrollmentTokenStyles,
    serverPageStyles,
  ];

  constructor() {
    super();
    new TableLabels(this);
    this.busy = false;
    this.actionError = null;
    this.issued = null;
    this.poll = poll(this, () => api.server(this.serverId));
  }

  updated(changed) {
    if (changed.has('serverId') && changed.get('serverId') !== undefined) {
      this.issued = null;
      this.poll.reset();
    }
  }

  /** Runs an action on the server, then reloads it. */
  async act(action) {
    this.busy = true;
    this.actionError = null;
    try {
      await action();
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  /** Revokes the server, closing its connection at once, or makes it active again. */
  async setStatus(server, status) {
    if (
      status === 'revoked' &&
      !(await confirmAction({
        heading: `Revoke server ${server.id}?`,
        message: 'Its connection closes at once and its clawlet cannot connect or enroll until the server is made active again. Its workspaces keep running as they are, unmanaged.',
        confirmLabel: 'Revoke server',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.act(() => api.setServerStatus(server.id, status));
  }

  /** Revokes the key the server last connected with: for a stolen or replaced certificate. */
  async revokeKey(server) {
    if (
      !(await confirmAction({
        heading: `Revoke the key of ${server.id}?`,
        message: 'Its connection closes at once. The server stays active: its clawlet connects again only after enrolling with a new key (an enrollment request you approve, or a new token), and the secrets sealed to the old key must be set again.',
        confirmLabel: 'Revoke key',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.act(() => api.revokeServerKey(server.id));
  }

  /** Takes over a held server: the desired state of the plane replaces the clawlet's. */
  async takeOver(server) {
    const unknown = server.unknownInstances ?? [];
    if (
      !(await confirmAction({
        heading: `Take over ${server.id}?`,
        message: unknown.length
          ? `The desired state of the plane replaces the one of the clawlet: ${unknown.join(', ')}, not assigned to this server here, will be stopped and removed (their state volumes are kept). To keep one, add it to this server first, with the same id.`
          : 'The desired state of the plane replaces the one of the clawlet: every workspace it runs that is not assigned to this server here will be stopped and removed (their state volumes are kept).',
        confirmLabel: 'Take over',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.act(() => api.takeOverServer(server.id));
  }

  async newToken(server) {
    const replaces = server.certificateExpiresAt || server.connection !== 'never';
    if (
      replaces &&
      !(await confirmAction({
        heading: 'Issue a new enrollment token?',
        message: `A clawlet that enrolls with the new token gets a new key: the current one of ${server.id} is revoked, and the secrets sealed to it must be set again.`,
        confirmLabel: 'Issue the token',
        tone: 'danger',
      }))
    ) {
      return;
    }
    this.busy = true;
    this.actionError = null;
    try {
      this.issued = await api.newEnrollmentToken(server.id);
    } catch (error) {
      this.actionError = error.message;
    } finally {
      this.busy = false;
      this.poll.refresh();
    }
  }

  /** The networks the server is expected to connect from, one per line or separated by commas. */
  saveNetworks(event, server) {
    event.preventDefault();
    const networks = event.target.elements.networks.value
      .split(/[\s,]+/)
      .map((network) => network.trim())
      .filter(Boolean);
    this.act(() => api.setExpectedNetworks(server.id, networks));
  }

  renderIdentity(server) {
    return html`
      <div class="card">
        <dl class="facts">
          <dt>Certificate</dt>
          <dd>${server.certificateExpiresAt ? html`expires ${when(server.certificateExpiresAt)}` : html`<span class="muted">none signed by this plane</span>`}</dd>
          <dt>Enrollment token</dt>
          <dd>${server.enrollmentTokenExpiresAt ? html`unused, valid until ${when(server.enrollmentTokenExpiresAt)}` : html`<span class="muted">none pending</span>`}</dd>
        </dl>
        <p class="muted">
          The clawlet renews its certificate by itself, with the same key, when a third of it is left. A clawlet with the bootstrap key that lost its identity, or whose certificate expired,
          asks for a new one: its request waits in <a href="/servers">Servers</a>. A new token is the other way to a new key.
        </p>
        <div class="actions">
          <button ?disabled=${this.busy || server.status !== 'active'} @click=${() => this.newToken(server)}>New enrollment token</button>
        </div>
      </div>
      <div class="card">
        <form class="networks" @submit=${(event) => this.saveNetworks(event, server)}>
          <label for="networks">Expected networks</label>
          <p class="muted">CIDR blocks or addresses, e.g. <code>203.0.113.0/24</code>, one per line: an enrollment request for this server from elsewhere is marked. Empty: only those of the installation.</p>
          <textarea id="networks" name="networks" rows="2" .value=${(server.expectedNetworks ?? []).join('\n')}></textarea>
          <button ?disabled=${this.busy}>Save networks</button>
        </form>
      </div>
    `;
  }

  /** What cannot be undone from here: apart, at the bottom. A revoked server is made active again from its head. */
  renderDangerZone(server) {
    if (server.status !== 'active') {
      return null;
    }
    return html`
      <div class="card danger-zone">
        <h3>Danger zone</h3>
        ${server.connection !== 'never'
          ? html`<div class="danger-action">
              <p>
                <strong>Revoke key</strong> For a stolen or replaced certificate: the connection closes, the server stays active, and its clawlet connects again only with a new
                key.
              </p>
              <button class="danger" ?disabled=${this.busy} @click=${() => this.revokeKey(server)}>Revoke key</button>
            </div>`
          : null}
        <div class="danger-action">
          <p><strong>Revoke server</strong> The connection closes and the clawlet cannot connect or enroll until the server is made active again; its workspaces keep running, unmanaged.</p>
          <button class="danger" ?disabled=${this.busy} @click=${() => this.setStatus(server, 'revoked')}>Revoke server</button>
        </div>
      </div>
    `;
  }

  /** A server whose clawlet is ahead of the plane: nothing is sent to it until it is taken over. */
  renderHeld(server) {
    const unknown = server.unknownInstances ?? [];
    return html`<div class="notice" data-tone="warning" role="status">
      <strong>The clawlet is ahead of the plane: nothing is sent to it</strong>
      ${(HOLD_REASON[server.holdReason] ?? HOLD_REASON.ahead)(server)}
      The clawlet keeps running its workspaces as it last knew them${unknown.length ? html`, among them ${unknown.map((name, i) => html`${i ? ', ' : ''}<code>${name}</code>`)}, not assigned to this server here` : null}.
      Add the workspaces to keep to this server (<a href="/workspaces/new?server=${encodeURIComponent(server.id)}">New workspace</a>, with the same id), then take the server over.
      <div class="actions"><button class="danger" ?disabled=${this.busy} @click=${() => this.takeOver(server)}>Take over</button></div>
    </div>`;
  }

  renderConnection(server) {
    return html`
      <div class="card">
        <dl class="facts">
          <dt>Connection</dt>
          <dd>${connectionBadge(server.connection)}</dd>
          ${server.connection === 'online'
            ? html`<dt>Connected</dt>
                <dd>${when(server.connectedSince)}</dd>`
            : html`<dt>Disconnected</dt>
                <dd>${when(server.disconnectedAt, 'never connected')}</dd>`}
          <dt>Last hello</dt>
          <dd>${when(server.helloAt, 'never')}</dd>
          <dt>Last address</dt>
          <dd>${server.lastAddress ? html`<code>${server.lastAddress}</code>` : html`<span class="muted">never connected</span>`}</dd>
          <dt>Enrollment</dt>
          <dd>${serverStatusBadge(server.status)}</dd>
          <dt>Clawlet</dt>
          <dd><code>${server.clawletVersion ?? '—'}</code></dd>
          <dt>Image versions</dt>
          <dd>${server.imageVersions?.length ? server.imageVersions.map((version) => html`<code>${version}</code> `) : html`<span class="muted">not declared yet</span>`}</dd>
          <dt>Capabilities</dt>
          <dd>${server.capabilities?.length ? server.capabilities.map((capability) => html`<code>${capability}</code> `) : html`<span class="muted">none</span>`}</dd>
          <dt>Generation</dt>
          <dd>${generation(server)}</dd>
        </dl>
      </div>
    `;
  }

  renderInstances(server) {
    if (server.instances.length === 0) {
      return html`<p class="empty">No workspace is assigned to this server.</p>`;
    }
    return html`
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Workspace</th>
              <th>Customer</th>
              <th>State</th>
              <th>Desired</th>
              <th>Applied</th>
              <th>Image</th>
            </tr>
          </thead>
          <tbody>
            ${server.instances.map(
              (instance) => html`
                <tr>
                  <td><a href="/workspaces/${instance.ref ?? instance.name}">${instance.displayName}</a><div class="muted"><code>${instance.name}</code></div></td>
                  <td>${instance.customer}</td>
                  <td>${stateBadge(instance.state)}</td>
                  <td>${revisionLabel(instance.desiredRevision)}</td>
                  <td>${revisionLabel(instance.appliedRevision)}</td>
                  <td><code>${instance.imageVersion}</code></td>
                </tr>
              `,
            )}
          </tbody>
        </table>
      </div>
    `;
  }

  render() {
    const crumbs = html`<nav class="crumbs" aria-label="Breadcrumb"><a href="/servers">Servers</a> / ${this.serverId}</nav>`;
    const pending = loadState(this.poll, 'the server');
    if (pending) {
      return html`${crumbs}<h1><code>${this.serverId}</code></h1>${pending}`;
    }
    const server = this.poll.value;
    return html`
      ${crumbs}
      <header class="page-head">
        <div>
          <h1><code>${server.id}</code> ${connectionBadge(server.connection)}</h1>
          <p class="lead">${server.name}</p>
        </div>
        <div class="actions">
          ${server.status === 'active'
            ? html`<a class="button primary" href="/workspaces/new?server=${encodeURIComponent(server.id)}">New workspace</a>`
            : html`<button class="primary" ?disabled=${this.busy} @click=${() => this.setStatus(server, 'active')}>Make active</button>`}
        </div>
      </header>
      ${staleWarning(this.poll)}
      ${this.actionError ? html`<div class="notice" data-tone="danger" role="alert"><strong>The action failed</strong>${this.actionError}</div>` : null}
      ${this.issued ? enrollmentTokenCard(this.issued, () => (this.issued = null)) : null}
      ${server.status === 'active'
        ? null
        : html`<div class="notice" data-tone="muted" role="status">
            <strong>Revoked</strong>Its clawlet cannot connect or enroll, and its workspaces run unmanaged, until the server is made active again.
          </div>`}
      ${server.aheadGeneration != null ? this.renderHeld(server) : null}
      ${server.lastError
        ? html`<div class="notice" data-tone="warning" role="status">
            <strong>The clawlet reported an error ${when(server.lastErrorAt)}</strong><code>${server.lastError}</code>
          </div>`
        : null}
      <section>
        <h2>Connection</h2>
        ${this.renderConnection(server)}
      </section>
      <section>
        <h2>Workspaces</h2>
        ${this.renderInstances(server)}
      </section>
      <section class="security">
        <h2>Identity &amp; security</h2>
        ${this.renderIdentity(server)} ${this.renderDangerZone(server)}
      </section>
    `;
  }
}

customElements.define('plane-server-page', ServerPage);
